import React, { useRef, useState } from "react";
import type { AotdDay } from "../printsApi";
import type { PrintEdition, PrintFormat } from "../printProducts";
import { ShowcaseScene } from "../showcase/ShowcaseScene";
import { SCENES, SCENES_FOR } from "../showcase/scenes";
import { FitFace } from "./FitFace";

// One format of a day, like a product's photos: the flat print face first, then its mockup
// scenes. Arrows and swipe move through them; the page scrolls normally over it.

const SWIPE_PX = 40;

export const PrintGallery: React.FC<{ day: AotdDay; format: PrintFormat; edition?: PrintEdition; thumbs?: boolean }> = ({
  day,
  format,
  edition = "print",
  thumbs = true,
}) => {
  const scenes = SCENES_FOR[format][edition] ?? SCENES_FOR[format].print ?? [];
  const count = scenes.length + 1;
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const go = (to: number) => setIndex((to + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx <= -SWIPE_PX) go(index + 1);
    else if (dx >= SWIPE_PX) go(index - 1);
  };

  return (
    <div className="tp-gallery">
      <div className="tp-gallery-main" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="tp-gallery-track" style={{ transform: `translateX(-${index * 100}%)` }}>
          <div className="tp-slide" aria-hidden={index !== 0}>
            <FitFace day={day} format={format} edition={edition} />
          </div>
          {scenes.map((scene, i) => (
            <div key={scene} className="tp-slide" aria-hidden={index !== i + 1}>
              <ShowcaseScene day={day} format={format} edition={edition} scene={scene} />
            </div>
          ))}
        </div>
        {count > 1 && (
          <>
            <button type="button" className="tp-arrow tp-arrow-prev" onClick={() => go(index - 1)} aria-label="Previous view">
              ‹
            </button>
            <button type="button" className="tp-arrow tp-arrow-next" onClick={() => go(index + 1)} aria-label="Next view">
              ›
            </button>
            <div className="tp-dots" aria-hidden="true">
              {Array.from({ length: count }, (_, i) => (
                <span key={i} className={i === index ? "is-active" : ""} />
              ))}
            </div>
          </>
        )}
      </div>
      {thumbs && count > 1 && (
        <div className="tp-thumbs" role="group" aria-label="views">
          <button type="button" className={index === 0 ? "is-active" : ""} onClick={() => setIndex(0)} aria-label="the print">
            <FitFace day={day} format={format} edition={edition} fill={0.7} />
          </button>
          {scenes.map((scene, i) => (
            <button
              key={scene}
              type="button"
              className={index === i + 1 ? "is-active" : ""}
              onClick={() => setIndex(i + 1)}
              aria-label={SCENES[scene].label}
            >
              <ShowcaseScene day={day} format={format} edition={edition} scene={scene} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrintGallery;
